<template>
  <div class="fc-drag-drop-file" data-testid="fc-drag-drop-file">
    <div
      v-if="localFiles.length"
      class="fc-drag-drop-file__list"
      data-testid="fc-drag-drop-file__list"
    >
      <div
        v-for="(file, index) in localFiles"
        :key="`${file.name}-${index}`"
        class="fc-drag-drop-file__row"
        :data-testid="`fc-drag-drop-file__row--${index}`"
      >
        <div class="fc-drag-drop-file__ext" aria-hidden="true">
          {{ fileExtension(file) }}
        </div>
        <div class="fc-drag-drop-file__content">
          <p class="fc-drag-drop-file__name" :title="file.name">
            {{ file.name }}
          </p>
        </div>
        <p
          v-if="formatFileSize(file)"
          class="fc-drag-drop-file__meta"
          data-testid="fc-drag-drop-file__meta"
        >
          {{ formatFileSize(file) }}
        </p>
        <button
          type="button"
          class="fc-drag-drop-file__delete"
          :aria-label="`Remove ${file.name}`"
          :data-testid="`fc-drag-drop-file__delete--${index}`"
          @click.stop="removeFile(index)"
        >
          <img
            src="/img/icons/fbc-icons/DeleteActions.svg"
            alt=""
            width="16"
            height="16"
          >
        </button>
      </div>
    </div>

    <div
      class="fc-drag-drop-file__dropzone"
      :class="{ 'fc-drag-drop-file__dropzone--dragging': isDragging }"
      data-testid="fc-drag-drop-file__dropzone"
      @dragover="dragover"
      @dragleave="dragleave"
      @drop="drop"
      @click="openFilePicker"
    >
      <input
        :id="inputId"
        ref="file"
        type="file"
        class="fc-drag-drop-file__input"
        data-testid="fc-drag-drop-file__input"
        :multiple="multiplefiles"
        :accept="accept"
        @change="onChange"
        @click.stop
      >
      <div class="fc-drag-drop-file__icon-wrap" aria-hidden="true">
        <img
          src="/img/icons/fbc-icons/UploadArrow.svg"
          alt=""
          class="fc-drag-drop-file__icon"
          width="20"
          height="20"
        >
      </div>
      <div class="fc-drag-drop-file__copy">
        <p class="fc-drag-drop-file__title">
          {{ isDragging ? draggingTitle : title }}
        </p>
        <p class="fc-drag-drop-file__hint">{{ hint }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  FC_DRAG_DROP_FILE_ACCEPT,
  FC_DRAG_DROP_FILE_DEFAULT_HINT,
  FC_DRAG_DROP_FILE_DEFAULT_TITLE,
  FC_DRAG_DROP_FILE_DRAGGING_TITLE,
} from './constants';
import { getFileExtension, isBlockedFile } from './utils';

let inputIdCounter = 0;

export default {
  name: 'FcDragDropFile',
  props: {
    files: {
      type: [Array, File],
      default: () => [],
    },
    accept: {
      type: String,
      default: FC_DRAG_DROP_FILE_ACCEPT,
    },
    multiplefiles: {
      type: Boolean,
      default: true,
    },
    resetTrigger: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: FC_DRAG_DROP_FILE_DEFAULT_TITLE,
    },
    hint: {
      type: String,
      default: FC_DRAG_DROP_FILE_DEFAULT_HINT,
    },
  },
  data() {
    return {
      isDragging: false,
      localFiles: [],
      inputId: `fc-drag-drop-file-input-${++inputIdCounter}`,
      draggingTitle: FC_DRAG_DROP_FILE_DRAGGING_TITLE,
    };
  },
  watch: {
    files: {
      immediate: true,
      handler(newVal) {
        if (!newVal || (Array.isArray(newVal) && newVal.length === 0)) {
          this.localFiles = [];
          if (this.$refs.file) this.$refs.file.value = '';
          return;
        }
        this.localFiles = Array.isArray(newVal) ? [...newVal] : [newVal];
      },
    },
    resetTrigger(newVal) {
      if (newVal) this.resetFiles();
    },
  },
  methods: {
    dragover(event) {
      event.preventDefault();
      this.isDragging = true;
    },
    dragleave(event) {
      event.preventDefault();
      this.isDragging = false;
    },
    drop(event) {
      event.preventDefault();
      this.isDragging = false;
      this.handleFiles(event.dataTransfer.files);
    },
    openFilePicker() {
      if (this.$refs.file) this.$refs.file.click();
    },
    onChange(event) {
      this.handleFiles(event.target.files);
      if (this.$refs.file) this.$refs.file.value = '';
    },
    handleFiles(files) {
      const newFiles = Array.from(files || []);
      let mergedFiles;

      if (!this.multiplefiles) {
        mergedFiles = newFiles.slice(0, 1);
      } else {
        mergedFiles = [...this.localFiles];
        newFiles.forEach((file) => {
          if (!mergedFiles.some((f) => f.name === file.name)) {
            mergedFiles.push(file);
          }
        });
      }

      const blockedFiles = mergedFiles.filter(isBlockedFile);
      const safeFiles = mergedFiles.filter((file) => !isBlockedFile(file));

      if (blockedFiles.length > 0) {
        const names = blockedFiles.map((file) => file.name).join(', ');
        const message = `These file types are not allowed: ${names}`;
        if (this.$store) {
          this.$store.commit('sbError', message);
        }
      }

      this.localFiles = safeFiles;
      this.$emit('update:files', this.localFiles);
    },
    removeFile(index) {
      const next = [...this.localFiles];
      next.splice(index, 1);
      this.localFiles = next;
      this.$emit('update:files', this.localFiles);
    },
    resetFiles() {
      this.localFiles = [];
      if (this.$refs.file) this.$refs.file.value = '';
      this.$emit('update:files', []);
    },
    fileExtension(file) {
      const ext = getFileExtension(file?.name);
      return (ext || 'FILE').toUpperCase().slice(0, 4);
    },
    formatFileSize(file) {
      const size = file?.size;
      if (size == null) return '';
      const numeric = Number(size);
      if (!Number.isFinite(numeric) || numeric <= 0) return '';
      if (numeric < 1024) return `${numeric} B`;
      if (numeric < 1024 * 1024) return `${(numeric / 1024).toFixed(1)} KB`;
      return `${(numeric / (1024 * 1024)).toFixed(1)} MB`;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './FcDragDropFile.scss';
</style>
